import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CONTACT_INFO } from '../data/mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import * as Icons from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Message sent successfully!');
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section className="pt-32 pb-16 px-6" style={{ background: 'radial-gradient(at 53% 78%, hsla(60,100%,50%,0.12) 0px, transparent 50%), radial-gradient(at 71% 91%, hsla(108,100%,50%,0.12) 0px, transparent 50%)' }}>
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div {...fadeInUp}>
            <Badge className="mb-5 bg-[rgba(148,242,127,0.12)] text-[rgb(13,121,22)] border-[rgba(148,242,127,0.3)] px-4 py-1.5 text-xs font-medium rounded-full">Get in Touch</Badge>
          </motion.div>
          <motion.h1 {...fadeInUp} transition={{ delay: 0.1 }}
            className="font-bold text-[rgb(0,55,32)] tracking-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', lineHeight: '0.92' }}>
            Contact Us
          </motion.h1>
          <motion.p {...fadeInUp} transition={{ delay: 0.2 }}
            className="text-[rgb(131,146,140)] max-w-[500px] mx-auto" style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)' }}>
            We're here to help. Reach out by phone or send us a message.
          </motion.p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-[1000px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-10">
            {/* Contact Info */}
            <motion.div {...fadeInUp}>
              <div className="space-y-6">
                <div className="bg-[rgba(0,0,0,0.02)] border border-black/10 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[rgba(148,242,127,0.12)] flex items-center justify-center">
                      <Icons.Phone className="w-5 h-5 text-[rgb(13,121,22)]" />
                    </div>
                    <div>
                      <p className="text-xs text-[rgb(131,146,140)]">Phone</p>
                      <a href={`tel:${CONTACT_INFO.phone}`} className="text-base font-semibold text-[rgb(0,55,32)] no-underline">{CONTACT_INFO.phone}</a>
                    </div>
                  </div>
                  <p className="text-xs text-[rgb(131,146,140)] ml-[52px]">Tap to call on mobile</p>
                </div>

                <div className="bg-[rgba(0,0,0,0.02)] border border-black/10 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-10 h-10 rounded-lg bg-[rgba(148,242,127,0.12)] flex items-center justify-center">
                      <Icons.Clock className="w-5 h-5 text-[rgb(13,121,22)]" />
                    </div>
                    <div>
                      <p className="text-xs text-[rgb(131,146,140)]">Business Hours</p>
                      <p className="text-base font-semibold text-[rgb(0,55,32)]">{CONTACT_INFO.hours}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[rgba(0,0,0,0.02)] border border-black/10 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-1">
                    <div className="w-10 h-10 rounded-lg bg-[rgba(148,242,127,0.12)] flex items-center justify-center shrink-0">
                      <Icons.MapPin className="w-5 h-5 text-[rgb(13,121,22)]" />
                    </div>
                    <div>
                      <p className="text-xs text-[rgb(131,146,140)]">Headquarters</p>
                      <p className="text-base font-semibold text-[rgb(0,55,32)]">{CONTACT_INFO.address}</p>
                      <p className="text-sm text-[rgb(0,55,32)]">{CONTACT_INFO.cityStateZip}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div {...fadeInUp} transition={{ delay: 0.1 }}>
              {sent ? (
                <div className="bg-[rgba(148,242,127,0.06)] border border-[rgba(148,242,127,0.3)] rounded-xl p-8 text-center">
                  <Icons.CheckCircle className="w-12 h-12 text-[rgb(13,121,22)] mx-auto mb-4" />
                  <h3 className="font-semibold text-[rgb(0,55,32)] text-lg mb-2">Message Sent!</h3>
                  <p className="text-sm text-[rgb(131,146,140)]">We'll get back to you within 1 business day.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white border border-black/10 rounded-xl p-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-[rgb(0,55,32)]">Name *</Label>
                      <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Your name" className="rounded-lg h-11" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-[rgb(0,55,32)]">Email *</Label>
                      <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="you@example.com" className="rounded-lg h-11" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-[rgb(0,55,32)]">Phone</Label>
                      <Input type="tel" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="(555) 123-4567" className="rounded-lg h-11" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-[rgb(0,55,32)]">Subject</Label>
                      <Input value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))} placeholder="What's this about?" className="rounded-lg h-11" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-[rgb(0,55,32)]">Message *</Label>
                    <Textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} placeholder="Tell us how we can help..." className="rounded-lg min-h-[120px]" />
                  </div>
                  <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-sm hover:shadow-md transition-shadow disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? 'Sending...' : 'Send Message'} {!loading && <Icons.Send className="w-4 h-4" />}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
